import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { type ReactNode, useState, useEffect } from 'react';

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  disableOnMobile?: boolean;
}

export const Tooltip = ({
  content,
  children,
  side = 'bottom',
  disableOnMobile = false,
}: TooltipProps) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(pointer: coarse)');
    setIsMobile(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // If disabled on mobile, just render children without tooltip
  if (isMobile && disableOnMobile) {
    return <>{children}</>;
  }

  // Mobile: Use Popover
  if (isMobile) {
    return (
      <PopoverPrimitive.Root>
        <PopoverPrimitive.Trigger asChild>{children}</PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            side={side}
            sideOffset={8}
            className="z-[9999] px-3 py-2 bg-[#1e293b]/95 backdrop-blur-md border-2 border-black/70 rounded-lg animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
            style={{
              boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.45)',
            }}
          >
            <div className="font-lilita text-sm text-white [text-shadow:0.5px_0.5px_0px_#000] [-webkit-text-stroke:0.5px_#000]">
              {content}
            </div>
            <PopoverPrimitive.Arrow
              className="fill-[#1e293b]"
              width={12}
              height={6}
            />
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    );
  }

  // Desktop: Use Tooltip
  return (
    <TooltipPrimitive.Root delayDuration={0}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={8}
          className="z-[9999] px-3 py-2 bg-[#1e293b]/95 backdrop-blur-md border-2 border-black/70 rounded-lg animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
          style={{
            boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.45)',
          }}
        >
          <div className="font-lilita text-sm text-white [text-shadow:0.5px_0.5px_0px_#000] [-webkit-text-stroke:0.5px_#000]">
            {content}
          </div>
          <TooltipPrimitive.Arrow
            className="fill-[#1e293b]"
            width={12}
            height={6}
          />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
};

export const TooltipProvider = TooltipPrimitive.Provider;
